import { postExchangeOffer } from "../../../app/api/exchangeOffer";
import { BaseAction } from "../../../core/actions/BaseAction";
import { convertISO8601ToDuration, convertISO8601ToMinutes, convertTimeToArabic, determinePassengerType, extractStops, formatDate, getNextDays } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { DefaultEnum, ServiceClassTypeEnum, SHUTTLE_PRODUCT_CODE, TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { getBookedAdmissionCodeByDirection } from "../../../Lib/Utils/booking";
import { AvailableDate, IBookingResponse, IDeterminePassenger, IExchangeOfferResponse, ILeg, IOffer, IPassenger, IPassengerSpecification, IQuickExchangeOfferRequest, ITrip, OffersRequest } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { TripOfferModel } from "../../../Orchestrator/Types/Types/OffersResponse";
import { DynamicPriceCategory, PriceCategory, PriceInfo, TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import dayjs from "dayjs";
import { compareByOrderThenPrice } from "../../../Lib/Utils/productOrderTag";

class FetchExchangeOffersAction extends BaseAction<IDefaultActionResult, {
    booking_id: string,
    exchangeOffersRequest: IQuickExchangeOfferRequest,
    booking: IBookingResponse,
    originalPriceOfExchangedItems: number,
    trainType: string,
    selectedDate: string,
    inboundSelectedDate: string
}> {

    override async run(): Promise<IDefaultActionResult> {
        let result: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }
        try {
            const { booking_id, exchangeOffersRequest, booking, originalPriceOfExchangedItems, trainType, selectedDate, inboundSelectedDate } = this.params!;
            updateRecoilState("trainAtom", []);
            const passengerSpecificationsTypes = booking.booking.passengers.map((passenger: IPassenger) => {
                const determinePassenger: IDeterminePassenger = {
                    age: passenger?.age,
                    cards: passenger?.cards || [],
                    prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                }
                return {
                    passengerType: determinePassengerType(determinePassenger),
                    externalRefId: passenger.externalRef
                }
            });

            // Set the offersRequestAtom based on exchangeOffersRequest data
            const offersRequest: OffersRequest = {
                tripSearchCriteria: exchangeOffersRequest.tripSearchCriteria,
                offerSearchCriteria: exchangeOffersRequest.offerSearchCriteria || { currency: 'AED' },
                anonymousPassengerSpecifications: exchangeOffersRequest.anonymousPassengerSpecifications
            };
            updateRecoilState("offersRequestAtom", offersRequest);

            // Before fetch
            //updateRecoilState("isLoadingAtom", true);

            // Update passengerSpecificationsTypesAtom
            updateRecoilState<IPassengerSpecification[]>("passengerSpecificationsTypesAtom", passengerSpecificationsTypes);

            // Set the outbound and inbound product codes if they exist. Resolve
            // the ADMISSION product per direction by trip coverage — not
            // products[0], which can be an ancillary (e.g. Business Lounge
            // Access), nor a fixed offer index, which round trips don't guarantee.
            const outboundProductCode = getBookedAdmissionCodeByDirection(booking, TripTypeEnum.OUT_BOUND);
            const inboundProductCode = getBookedAdmissionCodeByDirection(booking, TripTypeEnum.IN_BOUND);
            if (outboundProductCode) {
                updateRecoilState("outboundProductCodeAtom", outboundProductCode);
            }
            if (inboundProductCode) {
                updateRecoilState("inboundProductCodeAtom", inboundProductCode);
            }

            // Call the getExchangeOffers API
            const response = await postExchangeOffer(exchangeOffersRequest, booking_id);

            if (response.status === "ok" && response.answer) {
                const exchangeOffersData: IExchangeOfferResponse = response.answer;

                if (exchangeOffersData.exchangeOffers.length > 0) {

                    //Updates the offerAtom
                    updateRecoilState("offerAtom", exchangeOffersData.exchangeOffers);

                    // Process the exchange offers to create trip offers
                    const tripOffers = this.processOffers(
                        exchangeOffersData.trips as ITrip[],
                        exchangeOffersData.exchangeOffers
                    );

                    // Convert Offers To Trains
                    const convertedTrains = this.convertOffersToTrainsForChangeDate(tripOffers, trainType, originalPriceOfExchangedItems);
                    // Update the trainAtom with converted trains
                    updateRecoilState("trainAtom", convertedTrains);

                    // Update Available Dates
                    updateRecoilState("availableDatesAtom", this.getNextDaysForChangeDate(trainType, selectedDate, inboundSelectedDate));

                    result.status = "success";

                } else {
                    //Updates the offerAtom to empty array
                    updateRecoilState("offerAtom", null);
                    updateRecoilState("trainAtom", []);
                    result.status = "fail";
                    // showNotification(
                    //     "warning",
                    //     "No exchange offers found",
                    //     "Please select different criteria."
                    // );
                }
            } else {
                //Updates the offerAtom to empty array
                updateRecoilState("offerAtom", null);
                updateRecoilState("trainAtom", []);
                result.status = "fail";
                // showNotification(
                //     "warning",
                //     "No exchange offers found",
                //     "Please select different criteria."
                // );
            }
        } catch (error) {
            //Updates the offerAtom to empty array
            updateRecoilState("offerAtom", null);
            updateRecoilState("trainAtom", []);
            result.status = "fail";
            // showNotification(
            //     "warning",
            //     "No exchange offers found",
            //     "Please select different criteria."
            // );
        } finally {
            return result;
        }

    }

    getNextDaysForChangeDate(trainType: string, selectedDate: string, inboundSelectedDate: string): AvailableDate[] {
        const today = dayjs().startOf('day');

        if (trainType === TrainTypeEnum.outbound) {
            const referenceDate = selectedDate ? dayjs(selectedDate) : today;
            const sixtyDaysAgo = referenceDate.subtract(DefaultEnum.HorizontalCalendarDayScroll, "days").startOf("day");
            const start = dayjs.max(sixtyDaysAgo, today.startOf("day"));  // Ensure start is not before today
            const end = referenceDate.add(DefaultEnum.HorizontalCalendarDayScroll, "days").startOf("day");

            return getNextDays(
                start.format("YYYY-MM-DD"),
                end.format("YYYY-MM-DD")
            );
        } else {
            // For inbound, check against outbound date as min
            const start = dayjs(selectedDate ? selectedDate : inboundSelectedDate).startOf("day");
            const end = start.add(DefaultEnum.HorizontalCalendarDayScroll, "days").startOf("day");

            return getNextDays(
                start.format("YYYY-MM-DD"),
                end.format("YYYY-MM-DD")
            );
        }
    }
    processOffers(trips: ITrip[], offers: IOffer[]): TripOfferModel[] {
        const now = new Date();
        const tripOffers: TripOfferModel[] = [];

        const offersByTripAndProduct = new Map<string, IOffer[]>();
        const offersByTripId = new Map<string, IOffer[]>();
        for (const offer of offers) {
            const tripId = offer.tripCoverage?.coveredTripId;
            if (!tripId) continue;
            const code = offer.products?.[0]?.code;
            if (code) {
                const key = `${tripId}\0${code}`;
                let bucket = offersByTripAndProduct.get(key);
                if (!bucket) {
                    bucket = [];
                    offersByTripAndProduct.set(key, bucket);
                }
                bucket.push(offer);
            }
            let tripBucket = offersByTripId.get(tripId);
            if (!tripBucket) {
                tripBucket = [];
                offersByTripId.set(tripId, tripBucket);
            }
            tripBucket.push(offer);
        }

        const offersForTripProduct = (tripId: string, productCode: string) =>
            offersByTripAndProduct.get(`${tripId}\0${productCode}`) || [];

        trips.forEach(trip => {
            const tripStartTime = new Date(trip.startTime);

            // Skip trips that have already started today
            if (tripStartTime < now) {
                return;
            }

            // Collect ALL per-pax offers for each product type (supports INDIVIDUAL offerMode
            // where the exchange-offers API returns one offer per passenger).
            const shuttleOffers = offersForTripProduct(trip.id, SHUTTLE_PRODUCT_CODE);
            const standardOffers = offersForTripProduct(trip.id, "SFC");
            const standardProOffers = offersForTripProduct(trip.id, "SFF");
            const premiumOffers = offersForTripProduct(trip.id, "PFF");
            const standardDuoOffers = offersForTripProduct(trip.id, "STDNFDUO");
            const standardQuadOffers = offersForTripProduct(trip.id, "STDNFQUAD");
            const premiumDuoOffers = offersForTripProduct(trip.id, "PREMNFDUO");
            const premiumQuadOffers = offersForTripProduct(trip.id, "PREMNFQUAD");

            const tripOffersForLegs = offersByTripId.get(trip.id) || [];
            if (trip?.legs?.length > 0) {
                trip.legs = trip?.legs?.map(({ ...leg }: ILeg) => {
                    const newLeg = { ...leg }
                    const legOffers: IOffer[] | undefined = tripOffersForLegs.filter(offer => offer.tripCoverage?.coveredLegIds?.includes(leg.id));
                    if (legOffers) {
                        newLeg.legOffer = [...legOffers];
                    }
                    return newLeg;
                })
            }

            // Build per-product maps grouped by service class for INDIVIDUAL offers.
            // The dynamic-products code path in the train list reads these to populate
            // priceInfo.standardProducts / premiumProducts; without them the click handler
            // in TrainDetailsItem silently no-ops on the change-date page.
            const standardIndividualOffers: Map<string, IOffer[]> = new Map();
            const premiumIndividualOffers: Map<string, IOffer[]> = new Map();
            for (const offer of tripOffersForLegs) {
                const productCode = offer?.products?.[0]?.code;
                if (!productCode || productCode === SHUTTLE_PRODUCT_CODE) continue;
                const offerMode = offer.admissionOfferParts?.[0]?.offerMode;
                if (offerMode === "COLLECTIVE") continue;
                const serviceClassType = offer.products?.[0]?.serviceClass?.type
                    || offer.offerSummary?.overallServiceClass?.type;
                const targetMap = serviceClassType === ServiceClassTypeEnum.HIGH
                    ? premiumIndividualOffers
                    : standardIndividualOffers;
                if (!targetMap.has(productCode)) {
                    targetMap.set(productCode, []);
                }
                targetMap.get(productCode)!.push(offer);
            }

            tripOffers.push({
                trip,
                // Singular (first) for backward compat with TripOfferModel type
                shuttleOffer: shuttleOffers[0],
                standardOffer: standardOffers[0],
                standardProOffer: standardProOffers[0],
                premiumOffer: premiumOffers[0],
                standardDuoOffer: standardDuoOffers[0],
                standardQuadOffer: standardQuadOffers[0],
                premiumDuoOffer: premiumDuoOffers[0],
                premiumQuadOffer: premiumQuadOffers[0],
                // Plural arrays — carry all per-pax offers for INDIVIDUAL offerMode
                shuttleOffers,
                standardOffers,
                standardProOffers,
                premiumOffers,
                standardDuoOffers,
                standardQuadOffers,
                premiumDuoOffers,
                premiumQuadOffers,
                standardIndividualOffers,
                premiumIndividualOffers,
            })
        })
        return tripOffers;
    }

    convertOffersToTrainsForChangeDate(offersData: TripOfferModel[], type: string = TrainTypeEnum.outbound, originalPriceOfExchangedItems: number): TrainType[] {
        if (!offersData || offersData.length === 0) return [];
        const extractedTrains: TrainType[] = [];
        let idCounter = 1;
        offersData.forEach((offerModel) => {
            const trip: ITrip = offerModel.trip;

            // Handle delayed trips - use observed times
            let observedStartTime: string | undefined;
            let observedEndTime: string | undefined;
            let departureTimeSource: string;
            let arrivalTimeSource: string;

            if (trip.tripStatus?.delayed == true) {
                const firstLeg = trip?.legs[0];
                const lastLeg = trip?.legs[trip?.legs?.length - 1];

                // For delayed trips: use observed times for departure/arrival
                departureTimeSource = firstLeg?.timedLeg?.start?.serviceDeparture?.observedTime || trip.startTime;
                arrivalTimeSource = lastLeg?.timedLeg?.end?.serviceArrival?.observedTime || trip.endTime;

                // Store original trip times as observed times
                observedStartTime = trip.startTime;
                observedEndTime = trip.endTime;
            } else {
                // For non-delayed trips: use trip times as before
                departureTimeSource = trip.startTime;
                arrivalTimeSource = trip.endTime;
            }

            const departureTime = convertTimeToArabic(departureTimeSource);
            const arrivalTime = convertTimeToArabic(arrivalTimeSource);

            
            const duration = convertISO8601ToDuration(trip.duration);
            const durationInMinutes = convertISO8601ToMinutes(trip.duration);
            const stops = extractStops(trip);
            const trainNumbers = trip.legs[0].timedLeg.service.vehicleNumbers.join(", ");
            const transfers = trip?.transfers || 0;
            const priceInfo: PriceInfo = {
                standardProducts: [],
                premiumProducts: [],
                shuttle: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: "" },
                standardValue: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: "" },
                standardPro: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: "" },
                premium: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: "" },
                standardDuo: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: "" },
                standardQuad: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: "" },
                premiumDuo: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: "" },
                premiumQuad: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: "" },
            };

            const shuttleOffersRaw = offerModel.shuttleOffers || (offerModel.shuttleOffer ? [offerModel.shuttleOffer] : []);
            const standardOffersRaw = offerModel.standardOffers || (offerModel.standardOffer ? [offerModel.standardOffer] : []);
            const standardProOffersRaw = offerModel.standardProOffers || (offerModel.standardProOffer ? [offerModel.standardProOffer] : []);
            const premiumOffersRaw = offerModel.premiumOffers || (offerModel.premiumOffer ? [offerModel.premiumOffer] : []);
            const standardDuoOffersRaw = offerModel.standardDuoOffers || (offerModel.standardDuoOffer ? [offerModel.standardDuoOffer] : []);
            const standardQuadOffersRaw = offerModel.standardQuadOffers || (offerModel.standardQuadOffer ? [offerModel.standardQuadOffer] : []);
            const premiumDuoOffersRaw = offerModel.premiumDuoOffers || (offerModel.premiumDuoOffer ? [offerModel.premiumDuoOffer] : []);
            const premiumQuadOffersRaw = offerModel.premiumQuadOffers || (offerModel.premiumQuadOffer ? [offerModel.premiumQuadOffer] : []);

            const legs = trip?.legs || [];
            const shuttleLegIds = legs.filter(l => l?.timedLeg?.service?.mode?.ptMode === "BUS").map(l => l.id);
            const trainLegIds = legs.filter(l => l?.timedLeg?.service?.mode?.ptMode !== "BUS").map(l => l.id);

            const hasAllIds = (offers: IOffer[]) => offers.length > 0 && offers.every(o => !!o?.offerId);
            const coversAllLegs = (offers: IOffer[], requiredLegIds: string[]) => {
                if (!hasAllIds(offers) || requiredLegIds.length === 0) return false;
                const covered = new Set<string>();
                offers.forEach(o => (o?.tripCoverage?.coveredLegIds || []).forEach((id: string) => covered.add(id)));
                return requiredLegIds.every(id => covered.has(id));
            };

            const shuttleOffers = coversAllLegs(shuttleOffersRaw, shuttleLegIds) ? shuttleOffersRaw : [];
            const standardOffers = coversAllLegs(standardOffersRaw, trainLegIds) ? standardOffersRaw : [];
            const standardProOffers = coversAllLegs(standardProOffersRaw, trainLegIds) ? standardProOffersRaw : [];
            const premiumOffers = coversAllLegs(premiumOffersRaw, trainLegIds) ? premiumOffersRaw : [];
            const standardDuoOffers = coversAllLegs(standardDuoOffersRaw, trainLegIds) ? standardDuoOffersRaw : [];
            const standardQuadOffers = coversAllLegs(standardQuadOffersRaw, trainLegIds) ? standardQuadOffersRaw : [];
            const premiumDuoOffers = coversAllLegs(premiumDuoOffersRaw, trainLegIds) ? premiumDuoOffersRaw : [];
            const premiumQuadOffers = coversAllLegs(premiumQuadOffersRaw, trainLegIds) ? premiumQuadOffersRaw : [];

            const aggregateExchangeOffers = (offers: IOffer[] | undefined, subtractOriginal: boolean) => {
                if (!offers || offers.length === 0) return null;
                let totalExchange = 0;
                let seatsLeft: number | undefined = undefined;
                const offerIds: string[] = [];
                const descriptions: string[] = [];
                const scaleData = offers[0]?.exchangePrice?.scale || 2;

                offers.forEach(o => {
                    const scale = o?.exchangePrice?.scale || 2;
                    const amount = o?.exchangePrice?.amount || 0;
                    totalExchange += amount / ((100 * scale) / scale);

                    const availability = o?.reservationOfferParts?.[0]?.numericAvailability;
                    if (typeof availability === "number") {
                        seatsLeft = typeof seatsLeft === "number" ? Math.min(seatsLeft, availability) : availability;
                    }
                    if (o?.offerId) offerIds.push(o.offerId);
                    const desc = o?.products?.[0]?.descriptiveTexts?.[0]?.description;
                    if (desc) descriptions.push(desc);
                });

                // Preserve per-offer passenger mapping for correct exchange-operations payload building
                const offerPassengerRefs = offers
                    .filter(o => !!o?.offerId)
                    .map(o => ({ offerId: o.offerId, passengerRefs: o.passengerRefs || [] }));

                const finalPrice = Math.max(0, subtractOriginal ? (totalExchange - (originalPriceOfExchangedItems / ((100 * scaleData) / scaleData))) : totalExchange);
                return {
                    price: finalPrice,
                    isSoldOut: false,
                    discount: undefined,
                    seatsLeft: seatsLeft,
                    offerId: offerIds[0],
                    offerIds: offerIds,
                    offerPassengerRefs,
                    descriptionText: descriptions[0]
                } as PriceCategory;
            };

            const aggShuttle = aggregateExchangeOffers(shuttleOffers, false);
            const aggStandard = aggregateExchangeOffers(standardOffers, true);
            const aggStandardPro = aggregateExchangeOffers(standardProOffers, true);
            const aggPremium = aggregateExchangeOffers(premiumOffers, true);
            const aggStandardDuo = aggregateExchangeOffers(standardDuoOffers, true);
            const aggStandardQuad = aggregateExchangeOffers(standardQuadOffers, true);
            const aggPremiumDuo = aggregateExchangeOffers(premiumDuoOffers, true);
            const aggPremiumQuad = aggregateExchangeOffers(premiumQuadOffers, true);

            if (aggShuttle) priceInfo.shuttle = aggShuttle;
            if (aggStandard) priceInfo.standardValue = aggStandard;
            if (aggStandardPro) priceInfo.standardPro = aggStandardPro;
            if (aggPremium) priceInfo.premium = aggPremium;
            if (aggStandardDuo) priceInfo.standardDuo = aggStandardDuo;
            if (aggStandardQuad) priceInfo.standardQuad = aggStandardQuad;
            if (aggPremiumDuo) priceInfo.premiumDuo = aggPremiumDuo;
            if (aggPremiumQuad) priceInfo.premiumQuad = aggPremiumQuad;

            const buildDynamicProductsForChangeDate = (
                offerMap: Map<string, IOffer[]> | undefined,
                serviceClassType: string
            ): DynamicPriceCategory[] => {
                if (!offerMap || offerMap.size === 0) return [];
                const products: DynamicPriceCategory[] = [];
                offerMap.forEach((offersForCode, productCode) => {
                    const validOffers = coversAllLegs(offersForCode, trainLegIds) ? offersForCode : [];
                    const agg = aggregateExchangeOffers(validOffers, true);
                    if (agg) {
                        const firstProduct = offersForCode[0]?.products?.[0];
                        products.push({
                            ...agg,
                            productCode,
                            productName: firstProduct?.summary || firstProduct?.description || "",
                            serviceClassType,
                            descriptiveTexts: firstProduct?.descriptiveTexts || [],
                            isRecommended: (firstProduct?.productTags || []).some(
                                (tag: string) => tag?.toUpperCase()?.includes("RECOMMEND")
                            ),
                            productTags: firstProduct?.productTags || [],
                        });
                    }
                });
                // Sort by `order` product tag (CMS-curated display order)
                // first, falling back to price ascending when absent. Keeps
                // Change-Date in step with Search results.
                products.sort(compareByOrderThenPrice);
                return products;
            };

            priceInfo.standardProducts = buildDynamicProductsForChangeDate(
                offerModel.standardIndividualOffers,
                ServiceClassTypeEnum.STANDARD
            );
            priceInfo.premiumProducts = buildDynamicProductsForChangeDate(
                offerModel.premiumIndividualOffers,
                ServiceClassTypeEnum.HIGH
            );

            const train: TrainType = {
                id: idCounter++,
                departureTime,
                arrivalTime,
                duration,
                durationInMinutes,
                isSoldOut: priceInfo.standardProducts.length === 0 && priceInfo.premiumProducts.length === 0 && priceInfo.standardValue.isSoldOut && priceInfo.standardPro.isSoldOut && priceInfo.premium.isSoldOut,
                stops,
                trainNumbers,
                priceInfo,
                type: type,
                selectedPriceClass: null,
                transfers,
                legs: trip.legs
            };

            // Add observed times if trip is delayed
            if (observedStartTime) {
                (train as TrainType).observedStartTime = convertTimeToArabic(observedStartTime);
            }
            if (observedEndTime) {
                (train as TrainType).observedEndTime = convertTimeToArabic(observedEndTime);
            }

            if (coversAllLegs(shuttleOffers, shuttleLegIds)) train.shuttleOffers = shuttleOffers;
            if (coversAllLegs(standardOffers, trainLegIds)) train.standardOffers = standardOffers;
            if (coversAllLegs(standardProOffers, trainLegIds)) train.standardProOffers = standardProOffers;
            if (coversAllLegs(premiumOffers, trainLegIds)) train.premiumOffers = premiumOffers;
            if (coversAllLegs(standardDuoOffers, trainLegIds)) train.standardDuoOffers = standardDuoOffers;
            if (coversAllLegs(standardQuadOffers, trainLegIds)) train.standardQuadOffers = standardQuadOffers;
            if (coversAllLegs(premiumDuoOffers, trainLegIds)) train.premiumDuoOffers = premiumDuoOffers;
            if (coversAllLegs(premiumQuadOffers, trainLegIds)) train.premiumQuadOffers = premiumQuadOffers;

            // When coversAllLegs rejects shuttle offers (e.g. no BUS legs), re-attach raw offers only for zero-price edge cases so UI can still show options.
            if (!train.shuttleOffers && aggShuttle && aggShuttle.price === 0 && shuttleOffersRaw.length > 0) {
                train.shuttleOffers = shuttleOffersRaw;
            }
            if (!train.standardOffers && aggStandard && aggStandard.price === 0 && standardOffersRaw.length > 0) {
                train.standardOffers = standardOffersRaw;
            }
            if (!train.standardProOffers && aggStandardPro && aggStandardPro.price === 0 && standardProOffersRaw.length > 0) {
                train.standardProOffers = standardProOffersRaw;
            }
            if (!train.premiumOffers && aggPremium && aggPremium.price === 0 && premiumOffersRaw.length > 0) {
                train.premiumOffers = premiumOffersRaw;
            }
            if (!train.standardDuoOffers && aggStandardDuo && aggStandardDuo.price === 0 && standardDuoOffersRaw.length > 0) {
                train.standardDuoOffers = standardDuoOffersRaw;
            }
            if (!train.standardQuadOffers && aggStandardQuad && aggStandardQuad.price === 0 && standardQuadOffersRaw.length > 0) {
                train.standardQuadOffers = standardQuadOffersRaw;
            }
            if (!train.premiumDuoOffers && aggPremiumDuo && aggPremiumDuo.price === 0 && premiumDuoOffersRaw.length > 0) {
                train.premiumDuoOffers = premiumDuoOffersRaw;
            }
            if (!train.premiumQuadOffers && aggPremiumQuad && aggPremiumQuad.price === 0 && premiumQuadOffersRaw.length > 0) {
                train.premiumQuadOffers = premiumQuadOffersRaw;
            }

            extractedTrains.push(train);
        });
        return extractedTrains;
    }
}

export const fetchExchangeOffersAction = new FetchExchangeOffersAction()

